import { z } from 'zod';
import { defaultServer } from '~/api/def/servers/defaultServer';
import { getExecEndpoint } from '../../service/getExecEndpoint';
import { BqResponse } from './types';
import { DEFAULT_DASHBOARD_LAYOUT_SL } from '~/constants/constants';

export const configSchema = z.discriminatedUnion('isDynamic', [
  z.object({
    isDynamic: z.literal(true),
    x: z.number().int().optional(),
    y: z.number().int().optional(),
    w: z.number().int().optional(),
    h: z.number().int().optional(),
    id: z.string(),
    renderComponent: z.enum([
      'dynamic_table',
      'dynamic_histogram',
      'dynamic_pie_chart',
    ]),
    query: z.string(),
    shortDescription: z.string().optional(),
    isAggregate: z.boolean().optional(),
    primaryBatchType: z
      .enum(['current_holdings', 'universe', 'benchmark', 'proposed'])
      .optional(),
  }),
  z.object({
    isDynamic: z.literal(false).optional(),
    x: z.number().int().optional(),
    y: z.number().int().optional(),
    w: z.number().int().optional(),
    h: z.number().int().optional(),
    id: z.string(),
    renderComponent: z.undefined().optional(),
    query: z.undefined().optional(),
    shortDescription: z.undefined().optional(),
    primaryBatchType: z
      .enum(['current_holdings', 'universe', 'benchmark', 'proposed'])
      .optional(),
  }),
]);

export type Config = z.infer<typeof configSchema>;

export const dashboardEndpoint = {
  server: defaultServer,
  path: 'dashboard',
  description: 'Dashboard operations',
};

export interface DashboardEndpointDef {
  endpoint: typeof dashboardEndpoint;
  methods: {
    GET: {
      response: BqResponse<{
        data: {
          id: string;
          name: string;
          config: Config[];
          icon?: string;
          isDefault?: boolean;
          portfolioId?: string;
          type?: string;
        };
      }>;
    };
    GET_ALL: {
      response: BqResponse<{
        data: Array<{
          id: string;
          name: string;
          config: Config[];
          icon?: string;
          isDefault?: boolean;
          portfolioId?: string;
          type?: string;
        }>;
      }>;
    };
    POST: {
      payload: {
        id?: string;
        name: string;
        config?: Config[];
        icon?: string;
        isDefault?: boolean;
        portfolioId?: string;
        type?: string;
      };
      response: BqResponse<{
        data: {
          id: string;
          name: string;
          config: Config[];
          icon?: string;
          isDefault?: boolean;
          portfolioId?: string;
          type?: string;
        };
      }>;
    };
    PATCH: {
      payload: {
        name?: string;
        config: Config[];
      };
      response: BqResponse<{
        data: {
          id: string;
          name: string;
          config: Config[];
        };
      }>;
    };
    DELETE: {
      response: BqResponse<{
        data: void;
      }>;
    };
  };
}

const endpointDashboardExec =
  getExecEndpoint<DashboardEndpointDef>(dashboardEndpoint);

export const endpointDashboardGET = async (id: string) => {
  const response = await endpointDashboardExec<'GET'>({
    method: 'GET',
    urlExtra: `/${id}`,
  });

  if (!response) {
    throw new Error('Could not get dashboard');
  }

  if (response.success === 0) {
    throw new Error('Could not get dashboard');
  }

  // Migration: Check for ai-analysis widgets and replace with 3 new widgets if they don't exist
  const dashboard = response.data;
  if (dashboard.config) {
    // Remove pilot widgets from the config
    dashboard.config = dashboard.config.filter(
      (config) => !config.id.includes('pilot'),
    );
    const hasAiAnalysisWidget = dashboard.config.some((config) =>
      config.id.includes('ai-analysis'),
    );
    const hasRiskAlertsWidget = dashboard.config.some((config) =>
      config.id.includes('risk-alerts'),
    );
    const hasMarketAnalysisWidget = dashboard.config.some((config) =>
      config.id.includes('market-analysis'),
    );
    const hasNewsImpactWidget = dashboard.config.some((config) =>
      config.id.includes('news-impact-analysis'),
    );

    if (
      hasAiAnalysisWidget &&
      (!hasRiskAlertsWidget || !hasMarketAnalysisWidget || !hasNewsImpactWidget)
    ) {
      // Remove ai-analysis widgets and add the 3 new widgets
      const filteredConfig = dashboard.config.filter(
        (config) => !config.id.includes('ai-analysis'),
      );

      // Find the ai-analysis widget to get its position
      const aiAnalysisWidget = dashboard.config.find((config) =>
        config.id.includes('ai-analysis'),
      );

      if (aiAnalysisWidget) {
        // Create new widgets with basic positioning (will be corrected by reset layout)
        const newWidgets: Config[] = [
          {
            id: `risk-alerts-widget-${crypto.randomUUID()}`,
            x: 0, // Temporary position
            y: 0, // Temporary position
            w: 4,
            h: 7,
            isDynamic: false,
          },
          {
            id: `market-analysis-widget-${crypto.randomUUID()}`,
            x: 0, // Temporary position
            y: 0, // Temporary position
            w: 4,
            h: 8,
            isDynamic: false,
          },
          {
            id: `news-impact-analysis-widget-${crypto.randomUUID()}`,
            x: 0, // Temporary position
            y: 0, // Temporary position
            w: 4,
            h: 7,
            isDynamic: false,
          },
        ];

        // Create final config: remove ai-analysis + add new widgets
        const updatedConfig = [...filteredConfig, ...newWidgets];

        // Update the dashboard with the migrated config
        try {
          await endpointDashboardPATCH(id, { config: updatedConfig }, true);

          // Trigger reset layout to position widgets correctly
          const resetConfig = updatedConfig.map((currentWidget) => {
            // Find the default position for this widget by matching the widget type/name
            const defaultWidget = DEFAULT_DASHBOARD_LAYOUT_SL.find(
              (defaultItem) => currentWidget.id.includes(defaultItem.id),
            );

            if (defaultWidget) {
              return {
                ...currentWidget,
                x: defaultWidget.x,
                y: defaultWidget.y,
                w: defaultWidget.w,
                h: defaultWidget.h,
              };
            }
            return currentWidget;
          });

          // Update with reset positions
          await endpointDashboardPATCH(id, { config: resetConfig }, true);

          // Return the updated dashboard
          return {
            ...dashboard,
            config: resetConfig,
          };
        } catch (error) {
          console.error('Failed to migrate dashboard:', error);
          // Return original dashboard if migration fails
          return dashboard;
        }
      }
    }
  }

  return dashboard;
};

export const endpointDashboardGETAll = async () => {
  const response = await endpointDashboardExec<'GET'>({
    method: 'GET',
  });

  if (!response) {
    throw new Error('Could not get dashboards');
  }

  if (response.success === 0) {
    throw new Error('Could not get dashboards');
  }

  return response.data;
};

export const endpointDashboardDELETE = async (id: string) => {
  const response = await endpointDashboardExec<'DELETE'>({
    method: 'DELETE',
    urlExtra: `/${id}`,
  });

  if (!response) {
    throw new Error('Could not delete dashboard');
  }

  if (response.success === 0) {
    throw new Error('Could not delete dashboard');
  }

  return response.data;
};

export const endpointDashboardPOST = async (
  payload: DashboardEndpointDef['methods']['POST']['payload'],
) => {
  const response = await endpointDashboardExec<'POST'>({
    method: 'POST',
    payload,
  });

  if (!response) {
    throw new Error('Could not create/update dashboard');
  }

  if (response.success === 0) {
    throw new Error('Could not create/update dashboard');
  }

  return response.data;
};

export const endpointDashboardPATCH = async (
  id: string,
  payload: DashboardEndpointDef['methods']['PATCH']['payload'],
  replace?: boolean,
) => {
  const query = typeof replace === 'boolean' ? `?replace=${replace}` : '';

  const response = await endpointDashboardExec<'PATCH'>({
    method: 'PATCH',
    urlExtra: `/${id}${query}`,
    payload,
  });

  if (!response) {
    throw new Error('Could not create/update dashboard');
  }

  if (response.success === 0) {
    throw new Error('Could not create/update dashboard');
  }

  return response.data;
};
